import {
  Component,
  ChangeDetectionStrategy,
  Input,
  OnInit,
  HostListener,
  ViewChild,
  ElementRef,
  Self,
  ChangeDetectorRef,
  Output,
  EventEmitter,
} from '@angular/core';
import { AbstractControl, ControlValueAccessor, UntypedFormControl, NgControl } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { NgxMaskDirective } from 'ngx-mask';
import { merge, Subscription } from 'rxjs';
import { DEFAULT_MASK } from '../../common/constants/masks';
import { Delimiter } from '../../common/constants/delimiter';
import { COUNTRY_DIAL_CODES, CountryDialCodeDto } from '../../common/constants/country-dial-codes';
import { PHONE_NUMBER_PATTERN_WITH_CODE_COUNTRY } from '../../common/constants/validation-rules';
import { IconNames } from '../../common/constants/icon-names';
import { AutoUnsubscribe, markAsTouchedAndUpdate } from '../../common';
import { IntrackFeatureModule } from '../../intrack-feature.module';
import { CountryCodeComponent } from '../country-code/country-code.component';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';
import { markRequiredControlWithAsteriskSymbol } from '../../common/utils';

@Component({
  selector: 'ui-phone-input',
  imports: [
    IntrackFeatureModule,
    TranslateModule,
    NgxMatSelectSearchModule,
    NgxMaskDirective,
    CountryCodeComponent,
    SvgIconComponent,
  ],
  templateUrl: './phone-input.component.html',
  styleUrls: ['./phone-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
@AutoUnsubscribe
export class PhoneInputComponent implements OnInit, ControlValueAccessor {
  @Input() label = 'common.list.th.phoneNumber';
  @Input() testId = 'phone-number';
  @Input() disableField? = false;
  @Input() tooltipText?: string;
  @Input() showCheckmark?: boolean = false;

  @Input()
  set defaultCountryCode(defaultCountryCode: string) {
    if (!defaultCountryCode) {
      return;
    }

    if (!this.phoneNumberControl.value) {
      this.defaultCountryCodeValue = defaultCountryCode;

      this.countryCodeControl.setValue(this.defaultCountryCodeValue, {
        onlySelf: true,
      });
    } else {
      this.defaultCountryCodeValue = defaultCountryCode;
    }
  }

  @Input() set isValidationTriggered(trigger: boolean) {
    if (trigger) {
      markAsTouchedAndUpdate(this.phoneNumberControl);
      this.cd.markForCheck();
    }
  }

  @Output() selectedCountryCode = new EventEmitter<CountryDialCodeDto>();
  @Output() editedPhoneNumber = new EventEmitter<string>();

  @ViewChild(CountryCodeComponent)
  countryCodeComponent: CountryCodeComponent;

  @ViewChild('phoneNumberInput')
  phoneNumberInput: ElementRef;

  get isInputActive(): boolean {
    return this.isFocused || this.phoneNumberControl.value;
  }

  get floatLabel(): string {
    return this.isFocused ? 'always' : 'auto';
  }

  get hasRequiredError(): boolean {
    return this.phoneNumberControl.hasError('required') || this.ngControl?.control?.hasError('required');
  }

  get hasPatternError(): boolean {
    return (
      !this.hasRequiredError &&
      (this.phoneNumberControl.hasError('pattern') || this.ngControl?.control?.hasError('pattern'))
    );
  }

  phoneNumberControl: UntypedFormControl = new UntypedFormControl();
  countryCodeControl: UntypedFormControl = new UntypedFormControl(COUNTRY_DIAL_CODES[0].dialCodeValue);

  isFocused = false;
  hasClicked = false;

  defaultMask = DEFAULT_MASK;
  iconNames = IconNames;

  isFieldRequired: (control: AbstractControl) => string = markRequiredControlWithAsteriskSymbol;

  get isFieldInvalid(): boolean {
    return this.ngControl?.invalid;
  }

  private defaultCountryCodeValue = COUNTRY_DIAL_CODES[0].dialCodeValue;
  private readonly subscriptions: Subscription[] = [];

  constructor(@Self() public ngControl: NgControl, private cd: ChangeDetectorRef) {
    ngControl.valueAccessor = this;
  }

  @HostListener('click', ['$event'])
  onClickInside(event: Event): void {
    this.isFocused = true;
    this.hasClicked = true;

    event.stopPropagation();
  }

  @HostListener('document:click')
  onClickOutside(): void {
    if (!this.countryCodeComponent.matSelect.focused) {
      this.isFocused = false;
    }
  }

  ngOnInit(): void {
    this.initPhoneNumberAndCountryCodeValueObserver();
    this.initPhoneControlValidationSubscription();
  }

  onCountryCodeChanged(countryCode: CountryDialCodeDto): void {
    this.selectedCountryCode.emit(countryCode);
    setTimeout(() => {
      this.phoneNumberInput.nativeElement.focus();
    });
  }

  registerOnChange(fn: () => string): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  writeValue(fullPhoneNumber: string): void {
    if (fullPhoneNumber) {
      const [countryCode, phoneNumber] = this.parseFullPhoneNumber(fullPhoneNumber);

      this.countryCodeControl.setValue(countryCode);
      this.phoneNumberControl.setValue(phoneNumber);
    } else {
      this.onClearButtonClick();
    }
  }

  onClearButtonClick(): void {
    this.phoneNumberControl.setValue('', {
      emitEvent: false,
    });
    this.countryCodeControl.setValue(this.defaultCountryCodeValue);
  }

  onSaveButtonClick(): void {
    this.editedPhoneNumber.emit(this.getFullPhoneNumber());
  }

  private onChange: (value: string) => string;

  private onTouched: () => void;

  private initPhoneNumberAndCountryCodeValueObserver(): void {
    const subscription = merge(this.countryCodeControl.valueChanges, this.phoneNumberControl.valueChanges).subscribe(
      () => {
        if (this.phoneNumberControl.value) {
          const fullPhoneNumber = this.getFullPhoneNumber();

          this.onChange(fullPhoneNumber);
        } else {
          this.onChange(null);
        }
      },
    );

    this.subscriptions.push(subscription);
  }

  private getFullPhoneNumber(): string {
    return `+${this.countryCodeControl.value} ${this.phoneNumberControl.value || ''}`;
  }

  private parseFullPhoneNumber(fullPhoneNumber: string): [string, string] {
    if (!fullPhoneNumber.match(PHONE_NUMBER_PATTERN_WITH_CODE_COUNTRY)) {
      return [COUNTRY_DIAL_CODES[0].id, null];
    }

    const [countryCode, phoneNumber] = fullPhoneNumber.replace('+', '').split(Delimiter.Space);

    return [countryCode, phoneNumber];
  }

  private initPhoneControlValidationSubscription(): void {
    if (this.ngControl) {
      this.phoneNumberControl.setValidators(this.ngControl.control?.validator);

      const subscription = this.ngControl.statusChanges.subscribe(() => {
        this.cd.markForCheck();
      });

      this.subscriptions.push(subscription);
    }
  }
}
